import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import { useMemo, useState } from 'react';
import { StyledListContainerGrid } from './components/ListContainerGrid.styled';
import { Footer } from './footer/Footer';
import { Header } from './header/Header';
import type {
  AsyncSearchUnionProps,
  BaseListOption,
  BulkInputUnionProps,
  EnrichedListOption,
  HeaderSelectedListUnionProps,
  SelectedListUnionProps,
  VirtualiedListUnionTypeProps,
} from './types';
import { ITEM_SIZE } from './virtualList/constants';
import type { ListScrollOffsetProps, VirtualizedFlatListProps } from './virtualList/VirtualizedFlatList';
import { VirtualizedFlatList } from './virtualList/VirtualizedFlatList';

type ListProps<T> = {
  data: T[];
  isDisabled?: boolean;
  dataTest?: string;
  handleRowClick?: (id: string) => void;
  handleItemRowClick?: (item: T) => void;
  customSearch?: (data: T[], searchInput: string) => T[];
  errorMessage?: string;
  hideActionIcon?: boolean;
  customEmptyText?: string;
  fullWidth?: boolean;
  customItem?: VirtualizedFlatListProps<T>['customItem'];
  customPlaceholder?: string;
  itemSize?: number;
  footer?: React.ReactNode;
  onScroll?: (props: ListScrollOffsetProps) => void;
  allItemsCount?: number;
  isInfiniteScroll?: boolean;
} & SelectedListUnionProps &
  AsyncSearchUnionProps &
  BulkInputUnionProps;

export const FlatList = <T extends EnrichedListOption<BaseListOption>>(props: ListProps<T>) => {
  const {
    data,
    handleRowClick,
    handleItemRowClick,
    isSelectedList,
    isDisabled = false,
    isAsyncSearch,
    customEmptyText,
    dataTest,
    hasBulkInput,
    customSearch,
    errorMessage,
    hideActionIcon,
    fullWidth = false,
    customItem,
    customPlaceholder,
    itemSize = ITEM_SIZE,
    footer,
    onScroll,
    allItemsCount,
    isInfiniteScroll,
  } = props;
  const [searchInput, setSearchInput] = useState<string>('');

  const searchedData = useMemo(() => {
    if (isInfiniteScroll) {
      return data;
    }

    if (isAsyncSearch && !searchInput && customEmptyText) {
      return [];
    }

    if (customSearch) {
      return customSearch(data, searchInput);
    }

    return data.filter(obj => obj.name.toLowerCase().includes(searchInput.toLowerCase()));
  }, [data, searchInput, isAsyncSearch, customSearch, isInfiniteScroll, customEmptyText]);

  const asyncSearchProps: AsyncSearchUnionProps = isAsyncSearch
    ? {
        asyncSearchFn: props.asyncSearchFn,
        isAsyncSearch: true,
      }
    : { isAsyncSearch: false };

  const headerUnionProps: HeaderSelectedListUnionProps = isSelectedList
    ? {
        handleRemoveAll: props.handleRemoveAll,
        isSelectedList: true,
      }
    : { isSelectedList: false };

  const virtualizedListUnionProps: VirtualiedListUnionTypeProps = isSelectedList
    ? {
        actionType: props.actionType,
        isSelectedList: true,
      }
    : { isSelectedList: false };

  const bulkUnionProps: BulkInputUnionProps = hasBulkInput
    ? {
        handleBulkAdd: props.handleBulkAdd,
        handleBulkValidation: props.handleBulkValidation,
        hasBulkInput: true,
      }
    : { hasBulkInput: false };

  return (
    <Grid size={fullWidth ? 12 : 6}>
      <StyledListContainerGrid hasMarginLeft={isSelectedList} hasMarginRight={!isSelectedList} data-test={dataTest}>
        <Header
          {...headerUnionProps}
          {...asyncSearchProps}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          itemAmount={allItemsCount ?? searchedData.length}
          isDisabled={isDisabled}
          customPlaceholder={customPlaceholder}
        />

        <VirtualizedFlatList
          {...virtualizedListUnionProps}
          {...bulkUnionProps}
          data={searchedData}
          searchInput={searchInput}
          handleRowClick={handleRowClick as (id: string) => void}
          isDisabled={isDisabled}
          isAsyncSearch={!!isAsyncSearch}
          customEmptyText={customEmptyText}
          errorMessage={errorMessage}
          handleItemRowClick={handleItemRowClick}
          hideActionIcon={hideActionIcon}
          customItem={customItem}
          itemSize={itemSize}
          hasFooter={!!footer}
          onScroll={onScroll}
        />

        {footer && <Footer>{footer}</Footer>}
      </StyledListContainerGrid>

      {errorMessage && (
        <StyledListContainerGrid hasMarginLeft={isSelectedList} hasMarginRight={!isSelectedList}>
          <FormHelperText data-test="error-helper-text" error sx={{ marginRight: 1.5, overflowWrap: 'break-word' }}>
            {errorMessage}
          </FormHelperText>
        </StyledListContainerGrid>
      )}
    </Grid>
  );
};
